// ── useTileReadiness ─────────────────────────────────────────────────────────
// Tracks whether a Mapbox raster-tile source has finished loading its visible
// tiles.  Used by the crossfade controller to gate layer promotion: the old
// frame stays visible until the new frame's tiles are loaded (or a timeout
// fires), preventing the "tile pop" artifact where empty tiles flash on screen
// during source swaps.
//
// Implementation:  Mapbox GL fires `sourcedata` events with `isSourceLoaded`
// when a source finishes its tile queue.  We listen for those events scoped to
// the source ID we're watching, and flip a `ready` flag.
//
// The hook is intentionally minimal:
//   - No polling, no timers — purely event-driven.
//   - One instance per slot ("a" or "b").
//   - `arm(sourceId)` begins tracking; `ready` flips true when loaded.
//   - `reset()` clears tracking (used before loading the next frame).
//   - The caller manages the timeout externally (inside the crossfade effect).

import { useCallback, useEffect, useRef, useState } from "react";

export default function useTileReadiness(map, mapLoaded) {
  const [ready, setReady]       = useState(false);
  const watchingRef             = useRef(null);   // sourceId we're tracking

  // ── arm: begin watching a specific source ──────────────────────────────
  const arm = useCallback((sourceId) => {
    setReady(false);
    watchingRef.current = sourceId;

    // Immediately check — source may already be loaded (cache hit).
    if (map && mapLoaded) {
      try {
        if (map.isSourceLoaded(sourceId)) {
          setReady(true);
        }
      } catch {
        // Source may not exist yet — that's fine, the event will fire later.
      }
    }
  }, [map, mapLoaded]);

  // ── reset: stop watching, clear ready state ────────────────────────────
  const reset = useCallback(() => {
    watchingRef.current = null;
    setReady(false);
  }, []);

  // ── sourcedata listener ────────────────────────────────────────────────
  useEffect(() => {
    if (!map || !mapLoaded) return;

    function onSourceData(e) {
      if (!watchingRef.current) return;
      if (e.sourceId !== watchingRef.current) return;
      if (e.type !== "sourcedata" || !e.isSourceLoaded) return;
      setReady(true);
    }

    map.on("sourcedata", onSourceData);
    return () => map.off("sourcedata", onSourceData);
  }, [map, mapLoaded]);

  return { ready, arm, reset };
}
